/**
 * Visual swing of La Bolsa (issue #66): a damped pendulum excited by sudden changes of the vertical speed
 * of the plane. Only the render uses it; the core never knows. Angles are in radians, positive when the bag
 * swings towards the nose.
 */
export interface BagSwing {
  angle: number;
  velocity: number;
}

/** Natural frequency (rad/s) and damping ratio: back to rest in well under 1.5 s (GDD §4.1, proposal). */
export const SWING_OMEGA = 8;
export const SWING_DAMPING = 0.35;
/** Angular speed given to the bag per u/s of vertical speed change in one tick. */
export const SWING_KICK = 0.5;
/** Changes of vy below this (u/s per tick) are gravity, not a flap or a hit, and do not excite the bag. */
export const SWING_THRESHOLD = 2;
/** The bag never swings further than 35 degrees. */
export const SWING_MAX = (35 * Math.PI) / 180;

export const createBagSwing = (): BagSwing => ({ angle: 0, velocity: 0 });

/** One step of `dt` seconds with the vertical speed change `dvy` of that step (semi-implicit Euler). */
export function stepBagSwing(state: BagSwing, dt: number, dvy: number): BagSwing {
  let velocity = state.velocity;
  // Climbing leaves the bag behind (it swings away from the nose); diving swings it forward.
  if (Math.abs(dvy) > SWING_THRESHOLD) velocity -= SWING_KICK * dvy;
  velocity += (-SWING_OMEGA * SWING_OMEGA * state.angle - 2 * SWING_DAMPING * SWING_OMEGA * velocity) * dt;
  let angle = state.angle + velocity * dt;
  if (Math.abs(angle) > SWING_MAX) {
    angle = Math.sign(angle) * SWING_MAX;
    velocity = 0;
  }
  return { angle, velocity };
}
